import { ClinicMapLocation } from '@chiroup/core/types/ClinicMapLocation.type';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import HeatmapLayer from './HeatmapLayer';

// Fix for default marker icons in Leaflet with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export type ColorByOption = 'plan' | 'status' | 'schedule' | 'postType' | 'platform';
export type ViewMode = 'markers' | 'heatmap';
export type MapStyle = 'street' | 'satellite' | 'terrain' | 'dark';

// Map tile layer configurations
const MAP_TILES: Record<MapStyle, { url: string; attribution: string }> = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
};

interface Props {
  locations: ClinicMapLocation[];
  colorBy: ColorByOption;
  customColorSchemes?: Record<ColorByOption, Record<string, string>>;
  viewMode?: ViewMode;
  mapStyle?: MapStyle;
}

// Color schemes for different attributes
const COLOR_SCHEMES: Record<ColorByOption, Record<string, string>> = {
  plan: {
    ehr: '#10B981', // green
    premium: '#8B5CF6', // purple
    plus: '#3B82F6', // blue
    basic: '#6B7280', // gray
    unknown: '#9CA3AF',
  },
  status: {
    active: '#10B981', // green
    churned: '#EF4444', // red
    unknown: '#9CA3AF',
  },
  schedule: {
    A: '#F59E0B', // amber
    B: '#06B6D4', // cyan
    unknown: '#9CA3AF',
  },
  postType: {
    'Self-post': '#8B5CF6', // purple
    Automated: '#10B981', // green
    unknown: '#9CA3AF',
  },
  platform: {
    Facebook: '#1877F2', // facebook blue
    Instagram: '#E4405F', // instagram pink
    Linkedin: '#0A66C2', // linkedin blue
    multiple: '#8B5CF6', // purple for multiple
    unknown: '#9CA3AF',
  },
};

const getColorForLocation = (
  location: ClinicMapLocation,
  colorBy: ColorByOption,
  colorSchemes: Record<ColorByOption, Record<string, string>>
): string => {
  const scheme = colorSchemes[colorBy];

  switch (colorBy) {
    case 'plan': {
      const plan = location.plan?.toLowerCase() || 'unknown';
      return scheme[plan] || scheme.unknown;
    }
    case 'status': {
      const status = location.churned ? 'churned' : 'active';
      return scheme[status] || scheme.unknown;
    }
    case 'schedule': {
      const schedule = location.postingSchedule || 'unknown';
      return scheme[schedule] || scheme.unknown;
    }
    case 'postType': {
      const postType = location.postType || 'unknown';
      return scheme[postType] || scheme.unknown;
    }
    case 'platform': {
      const platforms = location.postingPlatforms || '';
      if (!platforms) return scheme.unknown;
      // Check if multiple platforms
      const platformList = platforms.split(';').filter(Boolean);
      if (platformList.length > 1) return scheme.multiple;
      return scheme[platformList[0]] || scheme.unknown;
    }
    default:
      return '#9CA3AF';
  }
};

// Create a marker icon - extracted for use in memoization
const createMarkerIcon = (color: string): L.DivIcon => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12z" fill="${color}"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>`,
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
};

// Cache for marker icons by color - avoids recreating icons on every render
const iconCache = new Map<string, L.DivIcon>();

const getOrCreateIcon = (color: string): L.DivIcon => {
  if (!iconCache.has(color)) {
    iconCache.set(color, createMarkerIcon(color));
  }
  return iconCache.get(color)!;
};

const ClinicMapView = ({
  locations,
  colorBy,
  customColorSchemes,
  viewMode = 'markers',
  mapStyle = 'street',
}: Props) => {
  // Center on continental US
  const center: [number, number] = [39.8283, -98.5795];
  const zoom = 4;
  const minZoom = 2; // Allow zooming out to see world
  const maxZoom = 18;

  // Use custom color schemes if provided, otherwise use defaults
  const colorSchemes = customColorSchemes || COLOR_SCHEMES;

  const markers = useMemo(() => {
    if (viewMode === 'heatmap') return null;

    return locations.map(location => {
      const color = getColorForLocation(location, colorBy, colorSchemes);
      return (
        <Marker
          key={location.id}
          position={[location.lat, location.lng]}
          icon={getOrCreateIcon(color)}
        >
          <Popup>
            <div className="min-w-[200px]">
              <h3 className="text-sm font-semibold text-gray-900">{location.name}</h3>
              <p className="text-xs text-gray-600">
                {location.city}
                {location.city && location.state ? ', ' : ''}
                {location.state}
              </p>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Plan:</span>
                  <span className="font-medium capitalize">{location.plan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span
                    className={`font-medium ${location.churned ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {location.churned ? 'Churned' : 'Active'}
                  </span>
                </div>
                {location.postingSchedule && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Schedule:</span>
                    <span className="font-medium">Schedule {location.postingSchedule}</span>
                  </div>
                )}
                {location.postType && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Post Type:</span>
                    <span className="font-medium">{location.postType}</span>
                  </div>
                )}
                {location.postingPlatforms && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Platforms:</span>
                    <span className="font-medium">
                      {location.postingPlatforms.replace(/;/g, ', ')}
                    </span>
                  </div>
                )}
                {location.usageHealth && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Usage Health:</span>
                    <span className="font-medium">{location.usageHealth}</span>
                  </div>
                )}
              </div>
            </div>
          </Popup>
        </Marker>
      );
    });
  }, [locations, colorBy, colorSchemes, viewMode]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      className="h-full w-full"
      scrollWheelZoom={true}
    >
      <TileLayer
        key={mapStyle}
        attribution={MAP_TILES[mapStyle].attribution}
        url={MAP_TILES[mapStyle].url}
      />
      {viewMode === 'markers' && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={40}
          showCoverageOnHover={false}
          spiderfyOnMaxZoom={true}
          disableClusteringAtZoom={10}
        >
          {markers}
        </MarkerClusterGroup>
      )}
      {viewMode === 'heatmap' && <HeatmapLayer locations={locations} />}
    </MapContainer>
  );
};

export default ClinicMapView;
export { COLOR_SCHEMES };
